import { formatDate, svenskMånad } from "helpers/date";
import { BaseClientEntity } from "./baseEntity.type";
import { Facility } from "./facility.type";
import {
  FiveYearsInspection,
  InspectionFile,
} from "./fiveYearsInspection/fiveYearsInspection.type";
import { SixMonthsInspection } from "./sixMonthsInspection/sixMonthsInspection.type";
import { TransportSedel } from "./transportSedel.type";
import { User } from "./user.type";
import {
  endOfMonth,
  endOfWeek,
  format,
  getWeek,
  getYear,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import { sv } from "date-fns/locale";
import { capitalizeFirstLetter } from "helpers/string";
import { Waste } from "./waste.type";
import { HasSentStatus } from "./transportDocument.type";
import { Customer } from "./customer.type";
import userStore, { getUser } from "store/user.store";
import { Role } from "./enums";
import { NvRapportering } from "./nvRapportering.type";
import { WaterMeasurementProtocol } from "./waterMeasurementProtocol/waterMeasurementProtocol.type";
import { WasteStationNotification } from "./wasteStationNotification.type";

export enum UppdragScheduleDateType {
  Week = "Week",
  Day = "Day",
  Month = "Month",
}

export type UppdragComment = {
  id: string;
  userUuid: string;
  comment: string;
  userName: string;
  createdAtUtc: string;
};

export enum UppdragStatus {
  Scheduled = "Scheduled",
  Started = "Started",
  Completed = "Completed",
}

export type TimeReportData = {
  reportedWorkingHours: string | null;
  reportedDrivingHours: string | null;
  estimatedWorkingHours: string | null;
  estimatedDrivingHours: string | null;
  isTimeReportConfirmed: boolean;
};

export type Uppdrag = BaseClientEntity & {
  description: string;
  referenceNumber: string;
  scheduleDate: Date;
  scheduleDateType: UppdragScheduleDateType;
  isKlarmarkerad: boolean;
  facility: Facility;
  assignedToUser?: User;
  createdByUser: User;
  sixMonthsInspectionList: SixMonthsInspection[];
  fiveYearsInspectionList: FiveYearsInspection[];
  transportSedelList: TransportSedel[];
  waterMeasurementProtocolList: WaterMeasurementProtocol[];
  comments: UppdragComment[];
  isPrioritized: boolean;
  status: UppdragStatus;
  fileList: InspectionFile[];
  wasteList: Waste[];
  areAllUppgiftsCompleted: boolean;
  emailToCustomerStatus: HasSentStatus;
  emailToWasteStationStatus: HasSentStatus;
  emailToParentCustomerStatus: HasSentStatus;
  timeReportData?: TimeReportData | null;
  isProxyUppdrag: boolean;
  parentCustomer?: Customer;
  hasPendingTodo?: boolean; // True om man ska visa en Dot i uppdragslistan exempelvis
  uppgiftCount?: number;
  externalId?: string;
  ombudsRapporteringList: NvRapportering[];
  transportRapporteringList: NvRapportering[];
  serialNumber: number;
  wasteStationNotification?: WasteStationNotification | null;
};

export const getUppdragDateAsString = (uppdrag?: Uppdrag) => {
  if (!uppdrag?.scheduleDate) {
    return "";
  }
  return uppdragScheduledDateAsString(
    uppdrag.scheduleDateType,
    uppdrag.scheduleDate,
  );
};

export const isNorva = (uppdrag: Uppdrag) =>
  uppdrag.facility.customer.company.isNorva;

export const uppdragScheduledDateAsString = (
  scheduleDateType: UppdragScheduleDateType,
  scheduleDate?: string | Date,
) => {
  if (!scheduleDate) {
    return "";
  }

  switch (scheduleDateType) {
    case UppdragScheduleDateType.Day:
      return formatDate(scheduleDate);
    case UppdragScheduleDateType.Week: {
      const d = new Date(scheduleDate);
      const monday = startOfWeek(d, { weekStartsOn: 1 });
      const sunday = endOfWeek(d, { weekStartsOn: 1 });
      const sameMonth = format(monday, "M") === format(sunday, "M");
      const sameYear = format(monday, "yyyy") === format(sunday, "yyyy");

      if (sameMonth && sameYear) {
        return `${format(monday, "d")}-${format(sunday, "d")} ${format(monday, "MMMM yyyy", { locale: sv })}`;
      } else if (!sameMonth && sameYear) {
        return `${format(monday, "d MMMM", { locale: sv })} - ${format(sunday, "d MMMM yyyy", { locale: sv })}`;
      } else {
        return `${format(monday, "d MMMM yyyy", { locale: sv })} - ${format(sunday, "d MMMM yyyy", { locale: sv })}`;
      }
    }
    case UppdragScheduleDateType.Month: {
      return `${capitalizeFirstLetter(svenskMånad(new Date(scheduleDate)))} ${getYear(scheduleDate)}`;
    }
  }
};

export const getWeekNumberString = (uppdrag?: Uppdrag) => {
  if (!uppdrag?.scheduleDate) {
    return "";
  }
  const d = new Date(uppdrag.scheduleDate);

  if (uppdrag.scheduleDateType === UppdragScheduleDateType.Month) {
    const firstMonday = startOfWeek(startOfMonth(d), { weekStartsOn: 1 });
    const lastMonday = startOfWeek(endOfMonth(d), { weekStartsOn: 1 });

    const firstWeek = getWeek(firstMonday, { locale: sv, weekStartsOn: 1 });
    const lastWeek = getWeek(lastMonday, { locale: sv, weekStartsOn: 1 });

    return `Vecka ${firstWeek} - ${lastWeek}`;
  }

  const monday = startOfWeek(d, { weekStartsOn: 1 });
  return `Vecka ${getWeek(monday, { locale: sv, weekStartsOn: 1 })}`;
};

export const getUppdragLabel = (uppdrag: Uppdrag) =>
  `Uppdrag #${uppdrag.serialNumber}`;

export const shouldShowEmailToCustomerPing = (uppdrag?: Uppdrag) =>
  uppdrag?.status === UppdragStatus.Completed &&
  uppdrag?.emailToCustomerStatus === HasSentStatus.NotSent;

export const shouldShowEmailToParentCustomerPing = (uppdrag?: Uppdrag) =>
  uppdrag?.status === UppdragStatus.Completed &&
  uppdrag.isProxyUppdrag &&
  uppdrag?.emailToParentCustomerStatus === HasSentStatus.NotSent;

export const shouldShowEmailToWasteStationPing = (uppdrag?: Uppdrag) =>
  uppdrag?.status === UppdragStatus.Completed &&
  uppdrag?.emailToWasteStationStatus === HasSentStatus.NotSent;

export const shouldShowWaitingForTimeReportConfirmationPing = (
  uppdrag?: Uppdrag,
) => {
  const timeReportData = uppdrag?.timeReportData;
  return (
    !!timeReportData &&
    (!!timeReportData.reportedWorkingHours ||
      !!timeReportData.reportedDrivingHours) &&
    !timeReportData.isTimeReportConfirmed
  );
};

/**
 *
 * @returns true if current user is admin or uppdrag was created by current user
 */
export const useIsCurrentUserAuthorizedForModifyingUppdrag = (
  uppdrag?: Uppdrag,
) => {
  if (!uppdrag) {
    return false;
  }
  const currentUser = userStore(getUser);
  if (!currentUser) {
    return false;
  }

  if (!uppdrag.createdByUser) {
    // Detta betyder att uppdraget är skapat av systemet vid migreringen
    return currentUser.role >= Role.Admin;
  }

  return (
    currentUser.role >= Role.Admin ||
    currentUser.id === uppdrag.createdByUser.id
  );
};
